import type React from "react"

import {cn} from "@/lib/cn"

/**
 * The round icon control from dized-next beta2 (its game-page back button and
 * hero actions): a 32px circle, hairline border, frosted fill, so it reads on
 * top of cover art as well as on the page.
 *
 * A class string as well as a component, because the same circle wraps a
 * `<Link>` (back, view toggles) as often as a `<button>`.
 */
export const iconButtonClassName = [
  "inline-flex size-8 shrink-0 items-center justify-center rounded-full",
  "border border-secondary-border bg-background/80 text-foreground shadow-sm backdrop-blur-md",
  "transition-colors hover:border-ring",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "aria-[current=true]:border-transparent aria-[current=true]:bg-action aria-[current=true]:text-action-foreground",
  "aria-pressed:border-transparent aria-pressed:bg-action aria-pressed:text-action-foreground",
  "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ")

/** A round icon button. `label` is required: an icon alone names nothing. */
export const IconButton = ({
  label,
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {label: string}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className={cn(iconButtonClassName, className)}
    {...props}
  >
    {children}
  </button>
)
